'use client';

import { useState } from 'react';
import Link from 'next/link';
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { createClient } from '@/lib/supabase/client';

interface ApplicationResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  createdAt: string;
  applicationType: 'user' | 'driver';
}

type StatusKey = 'pending' | 'under_review' | 'approved' | 'rejected';

const STATUS_CONFIG: Record<StatusKey, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
  message: string;
  nextSteps: string;
}> = {
  pending: {
    label: 'Pending',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="text-amber-500">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    message: 'Your application has been received and is in our queue.',
    nextSteps: 'Our team will begin reviewing your application shortly. You will receive an email notification once the review process starts. This typically takes 1–2 business days.',
  },
  under_review: {
    label: 'Under Review',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="text-blue-500">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    message: 'Great news! Your application is currently being reviewed by our team.',
    nextSteps: 'A member of our team is actively reviewing your application details. You can expect a decision within 2–3 business days. Please ensure your contact information is up to date.',
  },
  approved: {
    label: 'Approved',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="text-green-500">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    message: 'Congratulations! Your application has been approved.',
    nextSteps: 'Welcome to the GoGedit community! You will receive a follow-up email with your account details and onboarding instructions. If you haven\'t received it yet, please check your spam folder or contact us at info@gogedit.ca.',
  },
  rejected: {
    label: 'Not Approved',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="text-red-500">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    ),
    message: 'Unfortunately, your application was not approved at this time.',
    nextSteps: 'We appreciate your interest in GoGedit. Our team may have additional questions or requirements. Please contact us at info@gogedit.ca for more information about your application and potential next steps.',
  },
};

const TIMELINE_STEPS = ['Submitted', 'Pending', 'Under Review', 'Decision'];

function getTimelineStep(status: string): number {
  switch (status) {
    case 'pending': return 1;
    case 'under_review': return 2;
    case 'approved': case'rejected': return 3;
    default: return 0;
  }
}

export default function CheckStatusPage() {
  const [email, setEmail] = useState('');
  const [applicationType, setApplicationType] = useState<'user' | 'driver'>('user');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApplicationResult | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setResult(null);
    setNotFound(false);
    setError('');
    setEmailSent(false);
    setSearched(false);

    try {
      const supabase = createClient();
      const table = applicationType === 'user' ? 'user_applications' : 'driver_applications';

      const { data, error: dbError } = await supabase
        .from(table)
        .select('id, first_name, last_name, email, status, created_at')
        .eq('email', email.trim().toLowerCase())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (dbError || !data) {
        setNotFound(true);
        setSearched(true);
        setLoading(false);
        return;
      }

      const appResult: ApplicationResult = {
        id: data.id,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        status: data.status,
        createdAt: data.created_at,
        applicationType,
      };

      setResult(appResult);
      setSearched(true);

      // Send status check notification email
      try {
        const res = await fetch('/api/email/status-check-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: appResult.firstName,
            lastName: appResult.lastName,
            email: appResult.email,
            status: appResult.status,
            applicationType: appResult.applicationType,
            submittedAt: appResult.createdAt,
          }),
        });
        if (res.ok) setEmailSent(true);
      } catch {
        // Email notification is non-critical
      }
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const statusKey = (result?.status as StatusKey) || 'pending';
  const statusConfig = STATUS_CONFIG[statusKey] || STATUS_CONFIG.pending;
  const timelineStep = result ? getTimelineStep(result.status) : -1;

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <NavBar />
      <main className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50/30">
        {/* Hero */}
        <section className="bg-gradient-to-r from-deep-navy via-dark-navy to-brand-green text-white py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span className="text-sm font-medium">Application Tracker</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Check Your Application Status
            </h1>
            <p className="text-white/80 text-lg max-w-xl mx-auto">
              Enter your email address to look up the status of your GoGedit application — whether you applied as a User or Driver.
            </p>
          </div>
        </section>

        <div className="max-w-2xl mx-auto px-6 py-12">
          {/* Lookup Form */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
            <h2 className="text-xl font-heading font-bold text-dark-navy mb-6">Look Up Your Application</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Application Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  I applied as a:
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {(['user', 'driver'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setApplicationType(type)}
                      className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 font-semibold text-sm transition-all duration-200 ${
                        applicationType === type
                          ? 'border-brand-green bg-brand-green/5 text-brand-green' :'border-gray-200 text-gray-600 hover:border-brand-green/40'
                      }`}
                    >
                      {type === 'user' ? (
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <rect x="1" y="3" width="15" height="13" rx="2" />
                          <path d="M16 8h4l3 3v5h-7V8z" />
                          <circle cx="5.5" cy="18.5" r="2.5" />
                          <circle cx="18.5" cy="18.5" r="2.5" />
                        </svg>
                      )}
                      {type === 'user' ? 'User' : 'Driver'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter the email you used to apply"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-green transition-colors"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full py-3.5 bg-gradient-to-r from-brand-green to-brand-green/90 text-white font-bold rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" width="18" height="18" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Searching...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    Check Status
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Not Found */}
          {notFound && searched && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="text-gray-400">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-dark-navy mb-2">No Application Found</h3>
              <p className="text-gray-500 mb-6 leading-relaxed">
                We couldn't find a <strong>{applicationType}</strong> application associated with <strong>{email}</strong>. Please double-check your email address or try the other application type.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  href="/be-a-user"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-brand-green text-brand-green font-bold rounded-xl hover:bg-brand-green hover:text-white transition-all duration-200"
                >
                  Apply as User
                </Link>
                <Link
                  href="/be-a-driver"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-green text-white font-bold rounded-xl hover:bg-brand-green/90 transition-all duration-200"
                >
                  Apply as Driver
                </Link>
              </div>
            </div>
          )}

          {/* Result Card */}
          {result && (
            <div className="space-y-6">
              {/* Email Sent Notice */}
              {emailSent && (
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  A confirmation of this status check has been sent to <strong>{result.email}</strong>.
                </div>
              )}

              {/* Status Card */}
              <div className={`bg-white rounded-2xl shadow-lg border-2 ${statusConfig.borderColor} overflow-hidden`}>
                {/* Status Header */}
                <div className={`${statusConfig.bgColor} px-8 py-6 border-b ${statusConfig.borderColor}`}>
                  <div className="flex items-center gap-4">
                    {statusConfig.icon}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-1">Application Status</p>
                      <h3 className={`text-2xl font-heading font-bold ${statusConfig.color}`}>
                        {statusConfig.label}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="px-8 py-6 space-y-6">
                  {/* Applicant Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Applicant Name</p>
                      <p className="font-semibold text-dark-navy">{result.firstName} {result.lastName}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Application Type</p>
                      <p className="font-semibold text-dark-navy capitalize">{result.applicationType}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Email Address</p>
                      <p className="font-semibold text-dark-navy text-sm">{result.email}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Submitted On</p>
                      <p className="font-semibold text-dark-navy">{formatDate(result.createdAt)}</p>
                    </div>
                  </div>

                  {/* Status Timeline */}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Application Progress</p>
                    <div className="relative">
                      {/* Progress bar */}
                      <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-200">
                        <div
                          className="h-full bg-brand-green transition-all duration-500"
                          style={{ width: `${(timelineStep / (TIMELINE_STEPS.length - 1)) * 100}%` }}
                        />
                      </div>
                      <div className="relative flex justify-between">
                        {TIMELINE_STEPS.map((step, index) => {
                          const isCompleted = index <= timelineStep;
                          const isCurrent = index === timelineStep;
                          return (
                            <div key={step} className="flex flex-col items-center gap-2" style={{ width: `${100 / TIMELINE_STEPS.length}%` }}>
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 z-10 ${
                                  isCompleted
                                    ? 'bg-brand-green border-brand-green' :'bg-white border-gray-300'
                                } ${isCurrent ? 'ring-4 ring-brand-green/20' : ''}`}
                              >
                                {isCompleted ? (
                                  <svg width="14" height="14" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                ) : (
                                  <span className="w-2 h-2 rounded-full bg-gray-300" />
                                )}
                              </div>
                              <span className={`text-xs font-medium text-center leading-tight ${isCompleted ? 'text-brand-green' : 'text-gray-400'}`}>
                                {step}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Status Message */}
                  <div className={`${statusConfig.bgColor} rounded-xl p-5 border ${statusConfig.borderColor}`}>
                    <p className={`font-semibold ${statusConfig.color} mb-2`}>{statusConfig.message}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{statusConfig.nextSteps}</p>
                  </div>
                </div>
              </div>

              {/* Check Again */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setResult(null);
                    setSearched(false);
                    setEmail('');
                    setEmailSent(false);
                  }}
                  className="text-sm text-brand-green font-semibold hover:underline"
                >
                  ← Check another application
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <FooterSection />
    </>
  );
}
